import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { useResourceHubSection } from '../../hooks/useResourceHubSection';
import { useHome } from '../../hooks/useHome';
import { ResourceHubSectionSkeleton } from './ResourceHubSectionSkeleton';
import Button from '@/components/Button';
import { ResourceCard } from '@/components/ResourceCard';
import { ResourceHubItem } from '@/pages/resource-hub/shared/types';

const ResourceHubSectionComponent = () => {
  const { categories, categoriesWithResources } = useResourceHubSection();
  const { homePage } = useHome();

  const resourceSection = useMemo(() => homePage.homePage?.resourceSection, [homePage]);

  const maxResourcesPerCategory = useMemo(
    () =>
      categories.reduce((max, category) => {
        const resourcesCount = categoriesWithResources[category]?.resources?.length ?? 0;
        return resourcesCount > max ? resourcesCount : max;
      }, 0),
    [categories, categoriesWithResources],
  );

  if (categories.length === 0) {
    return null;
  }

  return (
    <AnimatedFadeInComponent fromBottom>
      <div>
        <h2 className="mb-sh-15 font-haffer text-sh-28-32 font-bold">{resourceSection?.title || 'Key dates'}</h2>

        {resourceSection?.description && (
          <p className="mb-sh-30 font-haffer text-sh-16-24 text-gray-600">{resourceSection.description}</p>
        )}
        <div className="grid grid-cols-1 gap-sh-30 overflow-visible md:grid-cols-2 lg:grid-cols-4">
          {categories.map((category) => {
            const { icon, resources = [], color } = categoriesWithResources[category] ?? {};
            const rowSpan = 1 + maxResourcesPerCategory;

            return (
              <div
                key={category}
                className="grid overflow-visible"
                style={{ gridRow: `span ${rowSpan}`, gridTemplateRows: 'subgrid' }}>
                <div className="flex items-center gap-2 border-b border-people-green-100 px-2 py-4">
                  {icon && <img src={icon} alt="Icon" className="size-6" />}
                  <span className="text-sh-sm text-dark-grey">{category}</span>
                </div>

                {Array.from({ length: maxResourcesPerCategory }).map((_, index) => {
                  const resource = resources[index];
                  if (!resource) return <div key={`${category}-empty-${index}`} />;

                  return (
                    <ResourceCard
                      key={resource.documentId}
                      resource={resource as unknown as ResourceHubItem}
                      categoryColor={color}
                      className="bg-white"
                    />
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="mt-sh-30">
          <Link to="/resource-hub">
            <Button theme="solid" className="w-full md:w-auto">
              {homePage.homePage?.resourceSection?.viewAllResourcesLabel || 'Explore our resource hub'}
            </Button>
          </Link>
        </div>
      </div>
    </AnimatedFadeInComponent>
  );
};

export const ResourceHubSection = () => {
  return (
    <Suspense fallback={<ResourceHubSectionSkeleton />}>
      <ResourceHubSectionComponent />
    </Suspense>
  );
};
